import { colors } from '@dixa/lib-shared-tokens';
import { produce } from 'immer';

import { BusinessHoursMessengerNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/businesshours';
import { CloseNodeMessengerAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/close';
import { ContactFormNodeMessengerAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/contactForm';
import { FiltersNodeMessengerAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/filters';
import { InitNodeMessengerAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/init';
import { IntegrationNodeMessengerAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/integration';
import { OpeningHoursDeprecatedNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/openingHours';
import { QueueNodeMessengerAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/queue';
import { TagsNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/tags';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { AvailabilityNodeAttributes } from 'components/settings/components/sections/dixaMessenger/views/flow/components/nodeTypeAvailability/canvas/availability';
import { ChatBotNodeAttributes } from 'components/settings/components/sections/dixaMessenger/views/flow/components/nodeTypeChatBot/canvas';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import type {
  GenericNodeAttributes,
  NodeAttributes,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';
import { FeatureFlagResult } from 'utils/featureFlagging/featureFlags';

import { MessengerCustomFormNodeAttributes } from './components/nodeTypeCustomForms/canvas/customFormCanvasNode';
import { InputNodeAttributes } from './components/nodeTypeInput/canvas/input';
import { MessengerMenuNodeAttributes } from './components/nodeTypeMessengerMenu/canvas/messengerMenu';
import { MessengerMessageNodeAttributes } from './components/nodeTypeMessengerMessage/canvas/messengerMessage';
import { URLTriggerNodeAttributes } from './components/nodeTypeUrlTrigger/canvas/url';

type Tooltip = {
  readonly title: string;
  readonly description: string;
};

type RestrictableFlowNodeTypes = Extract<
  FlowNodeTypes,
  | FlowNodeTypes.MESSENGER_MENU
  | FlowNodeTypes.MESSENGER_INPUT
  | FlowNodeTypes.INTEGRATION
  | FlowNodeTypes.MESSENGER_CHAT_BOT
  | FlowNodeTypes.MESSENGER_FORM
>;

type NodeRestrictionTooltipsMap = Record<RestrictableFlowNodeTypes, Tooltip>;

const nodeRestrictionTooltips: NodeRestrictionTooltipsMap = {
  [FlowNodeTypes.MESSENGER_MENU]: {
    title:
      'Upgrade to Advanced or Unlimited plan (contact your account manager)',
    description:
      'Menu enables you to create a path and clickable options for your users.',
  },
  [FlowNodeTypes.MESSENGER_INPUT]: {
    title:
      'Upgrade to Advanced or Unlimited plan (contact your account manager)',
    description:
      'Enable asking for user input like Order ID, Account Number, Shoe Size etc.',
  },
  [FlowNodeTypes.INTEGRATION]: {
    title: 'Upgrade to Unlimited plan (contact your account manager)',
    description:
      'Enable Integrating third-party systems or your own API to route conversations based on relevant customer information in Dixa (e.g. previous order, tracking links, etc.',
  },
  [FlowNodeTypes.MESSENGER_CHAT_BOT]: {
    title: 'Upgrade to Unlimited plan (contact your account manager)',
    description: 'Enable Integrating with a chatbot provider.',
  },
  [FlowNodeTypes.MESSENGER_FORM]: {
    title: '',
    description: '',
  },
};

const disabledColorNumber = hexStringToNumber(colors.neutral['400']);
const badgeContent = 'Upgrade to access';

type MessengerFlowNodeFeatureFlags = FeatureFlagResult<
  | 'chatbots-integration-settings'
  | 'conversation-assistant-for-dixa-messenger'
  | 'integrations-for-dixa-messenger'
  | 'has-custom-forms-feature'
>;

type FlowNode = {
  readonly type: FlowNodeTypes;
  readonly node: (
    flags: MessengerFlowNodeFeatureFlags,
  ) => GenericNodeAttributes | undefined;
};

const canvasNodeTypes: readonly FlowNode[] = [
  {
    type: FlowNodeTypes.INIT,
    node: () => InitNodeMessengerAttributes,
  },

  // ─── Green - Termination nodes ───────────────────────────────────────

  {
    type: FlowNodeTypes.QUEUE,
    node: () => QueueNodeMessengerAttributes,
  },
  {
    type: FlowNodeTypes.CONTACT_FORM,
    node: () => ContactFormNodeMessengerAttributes,
  },

  // ─── Red - Terminators ───────────────────────────────────────────────

  {
    type: FlowNodeTypes.CLOSE,
    node: () => CloseNodeMessengerAttributes,
  },

  // ─── Purple - Splitters ────────────────────────────────────────────────

  {
    type: FlowNodeTypes.MESSENGER_MENU,
    node: (flags) =>
      flags.conversationAssistantForDixaMessenger
        ? MessengerMenuNodeAttributes
        : disableNode(
            MessengerMenuNodeAttributes,
            FlowNodeTypes.MESSENGER_MENU,
          ),
  },
  {
    type: FlowNodeTypes.URL,
    node: () => URLTriggerNodeAttributes,
  },
  {
    type: FlowNodeTypes.BUSINESS_HOURS,
    node: () => BusinessHoursMessengerNodeAttributes,
  },
  {
    type: FlowNodeTypes.OPENING_HOURS,
    node: () => OpeningHoursDeprecatedNodeAttributes,
  },
  {
    type: FlowNodeTypes.FILTERS,
    node: () => FiltersNodeMessengerAttributes,
  },
  {
    type: FlowNodeTypes.MESSENGER_AVAILABILITY,
    node: () => AvailabilityNodeAttributes,
  },

  // ─── Orange - Inputs ──────────────────────────────────────────────────

  {
    type: FlowNodeTypes.MESSENGER_INPUT,
    node: (flags) =>
      flags.conversationAssistantForDixaMessenger
        ? InputNodeAttributes
        : disableNode(InputNodeAttributes, FlowNodeTypes.MESSENGER_INPUT),
  },
  {
    type: FlowNodeTypes.MESSENGER_FORM,
    node: (flags) =>
      flags.hasCustomFormsFeature
        ? MessengerCustomFormNodeAttributes
        : undefined,
  },

  // ─── Blue - Other ────────────────────────────────────────────────────

  {
    type: FlowNodeTypes.MESSENGER_MESSAGE,
    node: () => MessengerMessageNodeAttributes,
  },
  {
    type: FlowNodeTypes.INTEGRATION,
    node: (flags) =>
      flags.integrationsForDixaMessenger
        ? IntegrationNodeMessengerAttributes
        : disableNode(
            IntegrationNodeMessengerAttributes,
            FlowNodeTypes.INTEGRATION,
          ),
  },
  {
    type: FlowNodeTypes.TAGS,
    node: () => TagsNodeAttributes,
  },
  {
    type: FlowNodeTypes.MESSENGER_CHAT_BOT,
    node: (flags) =>
      flags.chatbotsIntegrationSettings
        ? ChatBotNodeAttributes
        : disableNode(ChatBotNodeAttributes, FlowNodeTypes.MESSENGER_CHAT_BOT),
  },
];

function disableNode(
  node: GenericNodeAttributes,
  type: FlowNodeTypes,
): GenericNodeAttributes {
  return produce(node, (draftNode) => {
    draftNode.disabled = true;
    draftNode.color = disabledColorNumber;
    draftNode.badgeContent = badgeContent;
    draftNode.helpTextTitle = nodeRestrictionTooltips[type]?.title ?? '';
    draftNode.helpText = nodeRestrictionTooltips[type]?.description ?? '';
  });
}

/**
 * Returns `canvasNodes` and `nodeTypes` enforcing user plan tier and feature
 * flags for the messenger flow.
 */
export function getNodesBasedOnFeatureFlags(
  featureFlags: MessengerFlowNodeFeatureFlags,
): Partial<NodeAttributes> {
  return canvasNodeTypes
    .map((nodeType) => ({
      type: nodeType.type,
      node: nodeType.node(featureFlags),
    }))
    .filter((nodeType) => nodeType.node !== undefined)
    .reduce((acc, nodeType) => {
      acc[nodeType.type] = nodeType.node;
      return acc;
    }, {});
}
